import {
  SearchTemplate,
  SearchTemplateForCreate,
  SearchTemplateForUpdate,
  SearchTreeNode,
} from '@/types';
import { DomainCode } from '@australiangreens/ag-internal-components';
import { useQuery } from '@tanstack/react-query';
import { logger, parseJSONWithoutException } from '@/util';
import callBackendApi, { useCallBackendApi } from './call';

export const createSearchTemplate = async (token: string, values: SearchTemplateForCreate) => {
  logger.debug('createSearchTemplate() called', values);

  const data: SearchTemplate = await callBackendApi({
    token,
    method: 'post',
    endpoint: '/searchtemplates',
    data: values,
  });
  return data;
};

export const getSearchTemplates = async (token: string, domainCode?: DomainCode) => {
  logger.debug('getSearchesBySavedBySub() called');

  const endpointURL = domainCode ? `/searchtemplates?domainCode=${domainCode}` : '/searchtemplates';
  const data: SearchTemplate[] = await callBackendApi({
    token,
    method: 'get',
    endpoint: endpointURL,
  });

  return data;
};

export const getSearchTemplateById = async (
  token: string,
  templateId: number
): Promise<SearchTemplate> => {
  const data: SearchTemplate = await callBackendApi({
    token,
    method: 'get',
    endpoint: `/searchtemplates/${templateId}`,
  });
  return data;
};

// Returns true if a duplicate encountered; false otherwise.

export const checkSearchTemplates = async (token: string, values: { name: string }) => {
  logger.debug('checkSearchTemplates() called', {});

  const { name } = values;

  const data = await callBackendApi({
    token,
    method: 'get',
    endpoint: `/searchtemplates/check?name=${encodeURIComponent(name)}`,
  });
  return data;
};

export const deleteSearchTemplate = async (token: string, searchTemplateId: number) => {
  logger.debug('deleteSearchTemplate() called', {});

  const data = await callBackendApi({
    token,
    method: 'delete',
    endpoint: `/searchtemplates/${searchTemplateId}`,
  });
  return data;
};

export const updateSearchTemplate = async (
  token: string,
  id: number,
  values: SearchTemplateForUpdate
) => {
  logger.debug('updateSearchTemplate() called', {});

  const data = await callBackendApi({
    token,
    method: 'put',
    endpoint: `/searchtemplates/${id}`,
    data: values,
  });
  return data;
};

export type SearchTemplateMetadata = {
  id: number | undefined;
  name: string | undefined;
  description: string | undefined;
  organisations: string[] | undefined;
};

export type LoadedSearchTemplate = {
  data: SearchTreeNode | undefined;
  metadata: SearchTemplateMetadata;
};

export function useLoadSearchTemplate(templateId: number) {
  const callBackendApi = useCallBackendApi<null, SearchTemplate>();
  const isValidTemplateId = !isNaN(templateId) && templateId > 0;

  return async (): Promise<LoadedSearchTemplate> => {
    if (!isValidTemplateId) {
      return {
        data: undefined,
        metadata: {
          id: undefined,
          description: undefined,
          name: undefined,
          organisations: undefined,
        },
      };
    }

    const searchTemplate = await callBackendApi({
      endpoint: `/searchtemplates/${templateId}`,
      method: 'get',
    });

    return {
      data: parseJSONWithoutException(searchTemplate?.searchTemplateJson ?? ''),
      metadata: {
        id: searchTemplate.id,
        name: searchTemplate.name,
        description: searchTemplate.description,
        organisations: searchTemplate.organisations,
      },
    };
  };
}

export const useSearchLoadTemplateQuery = (templateId: number) => {
  const loadSearchTemplate = useLoadSearchTemplate(templateId);
  return useQuery({
    queryKey: ['searchtemplate', templateId],
    queryFn: loadSearchTemplate,
    refetchOnWindowFocus: false,
  });
};
